import { useMemo, useState } from "react";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Grid,
  Table,
  TableRow
} from "semantic-ui-react";
import { AccordionTitleView, CommonTable, CustomButton, DropDown } from "../../../components";
import { InputNumber } from "../../../components/InputText/InputNumber";
import { FacilityHeatTable, HEAT_SOURCE_UNIT_TYPES, UNIT_TYPES } from "../../../config/constants";
import { DEFAULT_HEAT_SOURCE } from "../../../config/drawingConstants";
import { formatDecimalValue, validateFacilityNumber } from "../../../utils/utils";
import "./facilityForm.scss";

const FacilityHeatSource = ({
  heatType,
  defaultHeatType,
  setDefaultHeatType,
  errorsFacilityHeat,
  handleSubmitFacilityHeat,
  addFacilityHeat,
  currentFacilityHeatData,
  setDeleteHeatData,
  setVisibleDeleteHeatModal,
  control,
  setValue,
  setUnit,
  reset,
  getDefaultUnit,
  renderTableData = false
}: any) => {
  const [accordionEnabled, setAccordionEnabled] = useState<boolean>(false);
  const [enableEdit, setEnableEdit] = useState<boolean>(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const handleClick = () => {
    setAccordionEnabled(!accordionEnabled);
  };

  const selectedHeatType = useMemo(() => {
    return heatType?.find((item: any) => item?.value === String(defaultHeatType));
  }, [heatType, defaultHeatType]);

  const addFacilityHeatData = (data: any) => {
    const price = data?.Price ? Number(data?.Price) : 0;
    let updateData = {
      ...data,
      Price: price,
      UnitType: getDefaultUnit,
      Volumes: Number(data?.Volumes),
      HeatSource: Number(defaultHeatType) || DEFAULT_HEAT_SOURCE
    };
    if (enableEdit && editIndex !== null) {
      currentFacilityHeatData[editIndex] = updateData;
    } else {
      currentFacilityHeatData.push(updateData);
    }
    reset();
    setDefaultHeatType(DEFAULT_HEAT_SOURCE);
    setUnit(UNIT_TYPES[0].value);
    setEditIndex(null);
    setEnableEdit(false);
  };

  const groupHeatData = (data: any) => {
    let groupIngredientData = data.reduce((currentType: any, data: any, index: any) => {
      data.index = index;
      let checkDataIsAvailable = currentType?.find((other: any) => other.key === data.Year);
      if (currentType?.length === 0) {
        let facilityData = {
          key: data.Year,
          value: [data]
        };
        return [...currentType, facilityData];
      } else if (checkDataIsAvailable) {
        let currentIndex = currentType?.findIndex((other: any) => other.key === data.Year);
        let updatedArray = [];

        const updateArray = [...currentType[currentIndex].value, data];
        const updatedObject = {
          ...currentType[currentIndex],
          value: updateArray
        };
        updatedArray = [
          ...currentType.slice(0, currentIndex),
          updatedObject,
          ...currentType.slice(currentIndex + 1)
        ];
        return updatedArray;
      } else {
        let facilityData = {
          key: data.Year,
          value: [data]
        };

        return [...currentType, facilityData];
      }
    }, []);
    return groupIngredientData;
  };

  const setFacilityHeatDetails = (data: any) => {
    setValue("Percentage", data?.Percentage);
    setValue("Price", data?.Price);
    setValue("Year", data?.Year);
    setValue("Volumes", data?.Volumes);
    setUnit(data?.UnitType);
    setDefaultHeatType(data?.HeatSource);
    setEditIndex(data?.index);
    setEnableEdit(true);
  };

  const renderTableRow = (dataView: any, rowSpan: any, totalValue: any, onDelete: any) => {
    const heatTypeAsText = heatType?.find(
      (item: any) => item?.value === String(dataView?.HeatSource)
    )?.text;
    return (
      <TableRow key={dataView?.id || dataView?.index}>
        {rowSpan > 0 && (
          <Table.Cell rowSpan={rowSpan}>
            <p>{dataView?.Year ?? ""}</p>
          </Table.Cell>
        )}
        <Table.Cell>
          <p>{heatTypeAsText ?? 0.0}</p>
        </Table.Cell>
        <Table.Cell>
          <p>
            {isNaN(Number(dataView?.Volumes)) ? 0 : dataView?.Volumes ?? 0}
            <span className="unitTypeView">{dataView?.UnitType ?? ""}</span>
          </p>
        </Table.Cell>
        <Table.Cell>
          <p>{formatDecimalValue(dataView?.Price)}</p>
        </Table.Cell>
        <Table.Cell>
          <p>{formatDecimalValue(totalValue)}</p>
        </Table.Cell>
        <Table.Cell>
          {!renderTableData ? (
            <Grid>
              <Grid.Column computer={6} tablet={16} mobile={16}>
                <MdModeEditOutline
                  cursor="pointer"
                  size={24}
                  color="var(--tableEditIcon)"
                  onClick={() => setFacilityHeatDetails(dataView)}
                />
              </Grid.Column>
              <Grid.Column computer={6} tablet={16} mobile={16}>
                <MdDeleteOutline
                  cursor="pointer"
                  size={24}
                  color="var(--tableEditIcon)"
                  onClick={onDelete}
                />
              </Grid.Column>
            </Grid>
          ) : null}
        </Table.Cell>
      </TableRow>
    );
  };

  const loadTableData = () => {
    const groupFacilityByYear = groupHeatData(currentFacilityHeatData);
    return groupFacilityByYear.map((groupData: any, groupIndex: any) => {
      const rowCount = groupData?.value?.length || 0;
      return groupData?.value?.map((dataView: any, dataIndex: any) => {
        const isFirstRow = dataIndex === 0;
        const Volumes = dataView?.Volumes || 0;
        const price = dataView?.Price || 0;
        const totalValue = Volumes * price;

        const handleDelete = () => {
          setDeleteHeatData(dataView);
          setVisibleDeleteHeatModal(true);
        };
        return renderTableRow(dataView, isFirstRow ? rowCount : 0, totalValue, handleDelete);
      });
    });
  };

  return (
    <>
      <Accordion>
        <AccordionTitle active={accordionEnabled} onClick={handleClick}>
          <AccordionTitleView accordionEnabled={accordionEnabled} title={"Heat Sources"} />
        </AccordionTitle>
        <AccordionContent active={accordionEnabled}>
          <Grid>
            <Grid.Column
              computer={16}
              tablet={16}
              mobile={16}
              className={!renderTableData ? "userBankDetailsMain" : "recipeTableViewMain"}
            >
              <div>
                {!renderTableData ? (
                  <Grid>
                    <Grid.Column
                      computer={4}
                      tablet={8}
                      mobile={16}
                      className="paddingRemoveBottom"
                    >
                      <DropDown
                        labelName={"Source*"}
                        placeholder="Heat Source"
                        currentData={heatType}
                        defaultValue={selectedHeatType?.value || null}
                        customGridColumn={"customGridColomnType"}
                        handleChangeState={(e: any, { value }: any) => {
                          setDefaultHeatType(value);
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column
                      computer={4}
                      tablet={8}
                      mobile={16}
                      className="paddingRemoveBottom"
                    >
                      <InputNumber
                        control={control}
                        errors={errorsFacilityHeat.Year}
                        labelName={"Year*"}
                        placeholder="Year"
                        name="Year"
                        required={true}
                        errorMessage="Year is required"
                        isYearInput={true}
                      />
                    </Grid.Column>
                    <Grid.Column
                      computer={8}
                      tablet={8}
                      mobile={16}
                      className="paddingRemoveBottom"
                    />
                    <Grid.Column computer={4} tablet={8} mobile={16}>
                      <InputNumber
                        control={control}
                        errors={errorsFacilityHeat.Volumes}
                        labelName={"Volumes consumed"}
                        placeholder="Volumes"
                        name="Volumes"
                        required={false}
                        errorMessage="Volumes is required"
                        otherValidation={{
                          validate: {
                            valueCheck: (v: any) => validateFacilityNumber(v)
                          }
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column computer={2} tablet={8} mobile={16}>
                      <Grid.Column computer={4} tablet={8} mobile={16}>
                        <DropDown
                          labelName={"Unit Type"}
                          placeholder="Type"
                          currentData={HEAT_SOURCE_UNIT_TYPES}
                          defaultValue={getDefaultUnit}
                          customGridColumn={"customGridColomnType"}
                          handleChangeState={(e: any, { value }: any) => {
                            setUnit(value);
                          }}
                        />
                      </Grid.Column>
                    </Grid.Column>
                    <Grid.Column computer={4} tablet={8} mobile={16}>
                      <InputNumber
                        control={control}
                        errors={errorsFacilityHeat.Price}
                        labelName={"Price per unit"}
                        placeholder="Price"
                        name="Price"
                        otherValidation={{
                          validate: {
                            valueCheck: (v: any) => validateFacilityNumber(v)
                          }
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column computer={4} tablet={8} mobile={16}>
                      {enableEdit ? (
                        <CustomButton
                          theme="green"
                          customColumnStyle={"addUnitSize"}
                          title="Update"
                          onClick={handleSubmitFacilityHeat((data: any) => {
                            addFacilityHeatData(data);
                          })}
                          disabled={
                            errorsFacilityHeat && Object.keys(errorsFacilityHeat).length > 0
                              ? true
                              : false
                          }
                        />
                      ) : (
                        <CustomButton
                          theme="green"
                          customColumnStyle={"addUnitSize"}
                          icon="plus"
                          onClick={handleSubmitFacilityHeat((data: any) => {
                            addFacilityHeatData(data);
                          })}
                          disabled={
                            errorsFacilityHeat && Object.keys(errorsFacilityHeat).length > 0
                              ? true
                              : false
                          }
                        />
                      )}
                    </Grid.Column>
                  </Grid>
                ) : null}

                <Grid.Column
                  computer={16}
                  tablet={16}
                  mobile={16}
                  className={!renderTableData ? "productRegisterTable customTableBottom" : ""}
                >
                  {addFacilityHeat?.length > 0 && (
                    <CommonTable tableHeaderData={FacilityHeatTable}>{loadTableData()}</CommonTable>
                  )}
                </Grid.Column>
              </div>
            </Grid.Column>
          </Grid>
        </AccordionContent>
      </Accordion>
    </>
  );
};

export default FacilityHeatSource;
